/**
 * User service for handling user data operations
 */
import api from './api';

export interface User {
  id: number;
  email: string;
  name: string;
  profession?: string;
  experience?: string;
  company?: string;
  work_type?: string;
  city?: string;
  country?: string;
  zipcode?: string;
  latitude?: number | null;
  longitude?: number | null;
  bio?: string;
  linkedin?: string;
  skills: string[];
  profile_image_url?: string;
  cover_image_url?: string;
  is_current_user?: boolean;
  createdAt: string;
  updatedAt: string;
  connectionsCount?: number;
}

export interface LocationData {
  latitude?: number | null;
  longitude?: number | null;
  location_name?: string;
}

// Type definition for a received pending connection request
export interface ReceivedPendingRequest {
    id: number; // Connection ID
    note?: string | null;
    createdAt: string;
    requester: {
        id: number;
        name: string;
        profession?: string;
        company?: string;
    };
}

// Type definition for a sent pending connection request
export interface SentPendingRequest {
    id: number; // Connection ID
    note?: string | null;
    createdAt: string;
    recipient: {
        id: number;
        name: string;
        profession?: string;
        company?: string;
    };
}

// Type definition for the connection status
export type ConnectionStatus = 'connected' | 'pending_sent' | 'pending_received' | 'not_connected' | 'self';

/**
 * Get the current user's profile data
 */
export const getCurrentUser = async (): Promise<User> => {
  const response = await api.get('/users/me');
  if (response.data.success && response.data.user) {
    return response.data.user;
  }
  throw new Error(response.data.message || 'Failed to fetch user data');
};

/**
 * Get all users (for map display)
 */
export const getAllUsers = async (): Promise<User[]> => {
  const response = await api.get('/all-users');
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  throw new Error(response.data.message || 'Failed to fetch all users');
};

/**
 * Get user by ID
 */
export const getUserById = async (userId: number): Promise<User> => {
  const response = await api.get(`/users/${userId}`);
  if (response.data.success) {
    return response.data.data;
  }
  throw new Error(response.data.message || 'Failed to fetch user data');
};

/**
 * Update user profile
 */
export const updateUserProfile = async (userData: Partial<User> & { latitude?: number | null, longitude?: number | null }): Promise<User> => {
  const response = await api.put('/users/me', userData);
  if (response.data.success && response.data.user) {
    return response.data.user;
  }
  throw new Error(response.data.message || 'Failed to update profile');
};

/**
 * Get current user's location
 */
export const getUserLocation = async (): Promise<LocationData> => {
  const response = await api.get('/location');
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  throw new Error(response.data.message || 'Failed to fetch location data');
};

/**
 * Update user's location
 */
export const updateUserLocation = async (locationData: LocationData): Promise<LocationData> => {
  const response = await api.put('/location', locationData);
  if (response.data.success) {
    return response.data.data;
  }
  throw new Error(response.data.message || 'Failed to update location');
};

/**
 * Send connection request to user
 */
export const sendConnectionRequest = async (targetUserId: number, note?: string): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.post('/connections/request', { targetUserId, note });
    return {
      success: response.data.success,
      message: response.data.message
    };
  } catch (error: any) {
    console.error("Send connection request service error:", error);
    return { success: false, message: error.response?.data?.message || 'Failed to send request' };
  }
};

/**
 * Get pending connection requests (received and sent)
 */
export const getPendingRequests = async (): Promise<{
  received: ReceivedPendingRequest[];
  sent: SentPendingRequest[];
}> => {
  try {
    const response = await api.get('/connections/requests/pending');
    if (response.data.success && response.data.data) {
      return response.data.data; 
    }
    throw new Error(response.data.message || 'Failed to fetch pending requests');
  } catch (error: any) {
    console.error("[getPendingRequests] Error:", error);
    throw new Error(error.response?.data?.message || 'Failed to fetch pending requests');
  }
};

/**
 * Respond to a connection request (accept/decline)
 */
export const respondToRequest = async (requestId: number, action: 'accept' | 'decline'): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.post(`/connections/request/${requestId}/respond`, { action });
    return {
      success: response.data.success,
      message: response.data.message
    };
  } catch (error: any) {
    console.error("Respond connection request service error:", error);
    return { success: false, message: error.response?.data?.message || 'Failed to respond to request' };
  }
};

/**
 * Cancel a sent pending connection request
 */
export const cancelSentRequest = async (requestId: number): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.delete(`/connections/request/${requestId}`);
    return {
      success: response.data.success,
      message: response.data.message
    };
  } catch (error: any) {
    console.error("Cancel sent request service error:", error);
    return { success: false, message: error.response?.data?.message || 'Failed to cancel request' };
  }
};

/**
 * Get list of accepted connections (returns simplified user profiles)
 */
export const getConnections = async (searchQuery: string = ''): Promise<User[]> => {
  const params = new URLSearchParams();
  if (searchQuery) {
    params.set('search', searchQuery);
  }
  const response = await api.get('/connections', { params });
  if (response.data.success && response.data.data) {
    return response.data.data; // Assuming backend returns array of User objects
  }
  throw new Error(response.data.message || 'Failed to fetch connections');
};

/**
 * Remove an existing connection
 */
export const removeConnection = async (otherUserId: number): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.delete(`/connections/user/${otherUserId}`);
    return {
      success: response.data.success,
      message: response.data.message
    };
  } catch (error: any) {
    console.error("Remove connection service error:", error);
    return { success: false, message: error.response?.data?.message || 'Failed to remove connection' };
  }
};

/**
 * Check connection status between the current user and another user.
 */
export const checkConnectionStatus = async (otherUserId: number): Promise<ConnectionStatus> => {
  try {
    const response = await api.get(`/connections/status/${otherUserId}`);
    if (response.data.success && response.data.status) {
      return response.data.status as ConnectionStatus;
    }
    // If success is false or status is missing, treat as not connected or throw?
    // Let's throw an error for now to make issues more visible.
    throw new Error(response.data.message || 'Failed to check connection status');
  } catch (error: any) {
    console.error("Check connection status service error:", error);
    // Handle specific errors (e.g., 404 Not Found) if needed, 
    // otherwise rethrow or return a default status like 'not_connected'
    // For simplicity, rethrowing the error message.
    throw new Error(error.response?.data?.message || 'Failed to check connection status');
  }
};
